import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { ru } from 'date-fns/locale'
import axios from 'axios'
import Swal from 'sweetalert2'

import { detachEmployeeFromMerchant, getMerchantDetail, resetEmployeePassword } from './api'
import { MerchantSettings } from './MerchantSettings'
import MerchantIpAllowlist from './MerchantIpAllowlist'
import { IMerchant, IMerchantOperator } from './types'
import { ModalAddOperator } from './components/ModalAddOperator'
import LedgerWalletBalanceCard from '../../components/LedgerWalletBalance'
import { KTIcon } from '../../../../../_metronic/helpers'
import { ROLE_NAME } from '../../../../../constants'
import { IsGranted, ROLE, useAuth } from '../../../auth'
import { generatePassword } from '../../../../common/core/password'

const formatDate = (dateString: string) => {
  try {
    return format(new Date(dateString), 'dd.MM.yyyy HH:mm', { locale: ru })
  } catch {
    return '—'
  }
}

const getOperatorRoleLabel = (role: string): string => ROLE_NAME[role] ?? role

type EmployeeLike = Pick<IMerchantOperator, 'id' | 'uuid' | 'enabled' | 'roles' | 'createdAt' | 'updatedAt'>

interface EmployeeRowProps {
  employee: EmployeeLike
  onResetPassword?: (employee: EmployeeLike) => void
  onDetach?: (employee: EmployeeLike) => void
}

const EmployeeRow: React.FC<EmployeeRowProps> = ({ employee, onResetPassword, onDetach }) => (
  <tr>
    <td>
      <span className='text-dark fw-bold fs-6'>{employee.uuid}</span>
    </td>
    <td>
      {employee.roles
        .filter((r) => r !== 'ROLE_USER')
        .map((role, i) => (
          <span key={i} className='badge badge-light-primary me-1'>
            {getOperatorRoleLabel(role)}
          </span>
        ))}
    </td>
    <td>
      <span className={`badge ${employee.enabled ? 'badge-light-success' : 'badge-light-secondary'}`}>
        {employee.enabled ? 'Активен' : 'Неактивен'}
      </span>
    </td>
    <td>
      <span className='text-muted fw-semibold'>{formatDate(employee.createdAt)}</span>
    </td>
    <td>
      <span className='text-muted fw-semibold'>{formatDate(employee.updatedAt)}</span>
    </td>
    <td>
      <div className='d-flex gap-2'>
        {onResetPassword && (
          <button
            type='button'
            className='btn btn-sm btn-light-warning'
            onClick={() => onResetPassword(employee)}
          >
            <KTIcon iconName='key' className='fs-4 me-1' />
            Сбросить пароль
          </button>
        )}
        {onDetach && (
          <button
            type='button'
            className='btn btn-sm btn-light-danger'
            onClick={() => onDetach(employee)}
          >
            <KTIcon iconName='cross-circle' className='fs-4 me-1' />
            Открепить
          </button>
        )}
      </div>
    </td>
  </tr>
)

const MerchantDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { currentUser } = useAuth()
  // Сброс пароля остаётся только у admin/manager
  const canManageEmployees = IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER], currentUser)
  // Настройки самого мерчанта правит тот же круг ролей, что открыт ручке
  // PATCH /api/internal/merchant/{id}/deposit-precheck; менеджеру мерчантов бэкенд сужает
  // выборку его портфелем (merchants_managers), поэтому чужой мерчант вернёт 404.
  const canEditMerchantSettings = IsGranted(
    [ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_MANAGER],
    currentUser
  )
  // Добавление операторов доступно и менеджеру мерчантов — в рамках его мерчантов (CPAYMENT-1625)
  // Ограничение адресов интеграции — платформенное решение: менеджеру мерчантов блок не открыт,
  // вопреки обычной паре двух менеджеров. Список ролей совпадает с бэкендом (ROLE_MANAGER).
  const canEditMerchantIpAllowlist = IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER], currentUser)
  const canAddOperators = IsGranted(
    [ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_MANAGER],
    currentUser
  )
  const isMerchantManagerOnly =
    canAddOperators && !IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER], currentUser)

  // Открепление доступно тем же ролям, что и привязка (CPAYMENT-1673). Признака
  // «персональный / общий оператор» в данных нет — снять можно любого привязанного.
  // Менеджеру мерчантов бэкенд разрешает только ROLE_OPERATOR / ROLE_SENIOR_OPERATOR,
  // поэтому на прочих строках (например, оператор платежей мерчанта) кнопку не рисуем —
  // иначе она гарантированно отдавала бы 403.
  const canDetachOperator = (employee: EmployeeLike): boolean => {
    if (!canAddOperators) return false
    if (!isMerchantManagerOnly) return true

    return employee.roles.some(
      (role) => role === ROLE.ROLE_OPERATOR || role === ROLE.ROLE_SENIOR_OPERATOR
    )
  }
  const [merchant, setMerchant] = useState<IMerchant | null>(null)
  const [loading, setLoading] = useState(false)
  const [notFound, setNotFound] = useState(false)
  const [copySuccess, setCopySuccess] = useState(false)

  const fetchMerchant = useCallback(async () => {
    if (!id) return

    setLoading(true)
    setNotFound(false)
    try {
      const response = await getMerchantDetail(Number(id))
      if (response.data?.success && response.data.merchant) {
        setMerchant(response.data.merchant)
      } else {
        setNotFound(true)
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        setNotFound(true)
      } else if (axios.isAxiosError(error) && error.response?.status === 403) {
        navigate('/error/403')
      } else {
        setNotFound(true)
      }
    } finally {
      setLoading(false)
    }
  }, [id, navigate])

  useEffect(() => {
    fetchMerchant()
  }, [fetchMerchant])

  const showPasswordPopup = async (password: string) => {
    await Swal.fire({
      title: 'Новый пароль',
      html: `
        <div class="mb-3">
          <input
            id="employee-new-password"
            type="text"
            class="swal2-input"
            style="font-family: monospace;"
            readonly
            value="${password}"
          />
        </div>
        <div class="text-muted fs-7 mb-3">
          Сохраните пароль. После закрытия окна он не будет доступен.
        </div>
        <button type="button" id="employee-copy-password" class="swal2-styled">
          Копировать
        </button>
      `,
      showConfirmButton: true,
      confirmButtonText: 'Закрыть',
      showCancelButton: false,
      allowOutsideClick: false,
      didOpen: () => {
        const input = document.getElementById('employee-new-password') as HTMLInputElement | null
        const copyBtn = document.getElementById('employee-copy-password')

        if (!input || !copyBtn) {
          return
        }

        copyBtn.addEventListener('click', async () => {
          try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
              await navigator.clipboard.writeText(password)
              Swal.fire({
                toast: true,
                position: 'top',
                icon: 'success',
                title: 'Скопировано',
                showConfirmButton: false,
                timer: 2000,
              })
            } else {
              input.focus()
              input.select()
              Swal.fire({
                toast: true,
                position: 'top',
                icon: 'info',
                title: 'Скопируйте вручную',
                showConfirmButton: false,
                timer: 3000,
              })
            }
          } catch {
            input.focus()
            input.select()
            Swal.fire({
              toast: true,
              position: 'top',
              icon: 'info',
              title: 'Скопируйте вручную',
              showConfirmButton: false,
              timer: 3000,
            })
          }
        })
      },
      willClose: () => {
        const input = document.getElementById('employee-new-password') as HTMLInputElement | null
        if (input) {
          input.value = ''
        }
      },
    })
  }

  const handleResetPassword = async (employee: { id: number; uuid: string }) => {
    const result = await Swal.fire({
      title: `Сбросить пароль для ${employee.uuid}?`,
      icon: 'question',
      showCancelButton: true,
      cancelButtonText: 'Отмена',
      confirmButtonText: 'Сбросить',
      confirmButtonColor: '#f1416c',
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        const password = generatePassword()

        try {
          const response = await resetEmployeePassword(employee.id, password)

          if (!response.data || (response.data && response.data.success === false)) {
            throw new Error('reset_failed')
          }

          return password
        } catch (error) {
          Swal.close()
          Swal.fire({
            toast: true,
            position: 'top',
            icon: 'error',
            title: 'Не удалось сбросить пароль',
            showConfirmButton: false,
            timer: 3000,
          })

          throw error
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    })

    if (result.isConfirmed && typeof result.value === 'string') {
      await showPasswordPopup(result.value)
    }
  }

  const handleDetachEmployee = async (employee: EmployeeLike) => {
    if (!merchant) return

    const result = await Swal.fire({
      title: `Открепить ${employee.uuid} от мерчанта?`,
      text: 'Сотрудник потеряет доступ к этому мерчанту. Учётная запись и остальные привязки сохранятся.',
      icon: 'warning',
      showCancelButton: true,
      cancelButtonText: 'Отмена',
      confirmButtonText: 'Открепить',
      confirmButtonColor: '#f1416c',
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          const response = await detachEmployeeFromMerchant(employee.id, merchant.id)

          if (!response.data || response.data.success === false) {
            throw new Error('detach_failed')
          }

          return true
        } catch (error) {
          Swal.close()
          Swal.fire({
            toast: true,
            position: 'top',
            icon: 'error',
            title:
              axios.isAxiosError(error) && error.response?.status === 403
                ? 'Недостаточно прав для открепления'
                : 'Не удалось открепить сотрудника',
            showConfirmButton: false,
            timer: 3000,
          })

          throw error
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    })

    if (result.isConfirmed) {
      await fetchMerchant()
    }
  }

  const handleCopyToken = async () => {
    if (!merchant?.token) return
    try {
      await navigator.clipboard.writeText(merchant.token)
      setCopySuccess(true)
      setTimeout(() => setCopySuccess(false), 2000)
    } catch {
      // ignore
    }
  }

  if (loading) {
    return (
      <div className='d-flex justify-content-center py-10'>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Загрузка...</span>
        </div>
      </div>
    )
  }

  if (notFound) {
    return (
      <div className='d-flex flex-column align-items-center py-15'>
        <KTIcon iconName='information-5' className='fs-3x text-danger mb-5' />
        <h3 className='text-gray-800 fw-bold mb-3'>Мерчант не найден</h3>
        <p className='text-muted mb-7'>Запрошенный мерчант не существует или был удалён.</p>
        <button className='btn btn-primary' onClick={() => navigate('/settings/merchants')}>
          <KTIcon iconName='arrow-left' className='fs-4 me-1' />
          Назад к списку
        </button>
      </div>
    )
  }

  if (!merchant) return null

  return (
    <div>
      <div className='d-flex align-items-center mb-7'>
        <button
          className='btn btn-sm btn-light me-3'
          onClick={() => navigate('/settings/merchants')}
        >
          <KTIcon iconName='arrow-left' className='fs-4' />
          Назад к списку
        </button>
        <h1 className='page-heading fw-bold fs-2 text-dark mb-0'>{merchant.title}</h1>
      </div>

      {/* Блок 1: Информация о мерчанте */}
      <div className='card mb-7'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>Информация о мерчанте</span>
          </h3>
        </div>
        <div className='card-body py-4'>
          <div className='row mb-6'>
            <div className='col-lg-4'>
              <div className='fw-bold text-gray-600 mb-2 fs-7 text-uppercase'>Название</div>
              <div className='fw-bold fs-6 text-gray-800'>{merchant.title}</div>
            </div>
            <div className='col-lg-4'>
              <div className='fw-bold text-gray-600 mb-2 fs-7 text-uppercase'>Статус</div>
              <span
                className={`badge ${merchant.enabled ? 'badge-light-success' : 'badge-light-danger'}`}
              >
                {merchant.enabled ? 'Включен' : 'Выключен'}
              </span>
            </div>
            <div className='col-lg-4'>
              <div className='fw-bold text-gray-600 mb-2 fs-7 text-uppercase'>
                Анонимные платежи
              </div>
              <div className='fw-semibold fs-6 text-gray-800'>
                {merchant.allowAnonymousPayments ? 'Да' : 'Нет'}
              </div>
            </div>
          </div>

          <div className='row mb-6'>
            <div className='col-lg-12'>
              <div className='fw-bold text-gray-600 mb-2 fs-7 text-uppercase'>Токен</div>
              <div className='d-flex align-items-center gap-3'>
                <code className='fs-6 text-gray-800 bg-light-primary px-3 py-1 rounded'>
                  {merchant.token}
                </code>
                <button
                  className={`btn btn-sm ${copySuccess ? 'btn-success' : 'btn-light-primary'}`}
                  onClick={handleCopyToken}
                  title='Скопировать токен'
                >
                  <KTIcon
                    iconName={copySuccess ? 'check' : 'copy'}
                    className='fs-4 me-0'
                  />
                  {copySuccess ? 'Скопировано' : 'Копировать'}
                </button>
              </div>
            </div>
          </div>

          <div className='row mb-6'>
            <div className='col-lg-4'>
              <div className='fw-bold text-gray-600 mb-2 fs-7 text-uppercase'>Хост страницы</div>
              <div className='fw-semibold fs-6 text-gray-800'>
                {merchant.userPageHost ?? '—'}
              </div>
            </div>
            <div className='col-lg-4'>
              <div className='fw-bold text-gray-600 mb-2 fs-7 text-uppercase'>Платформа</div>
              <div className='fw-semibold fs-6 text-gray-800'>
                {merchant.platform?.uuid ?? '—'}
              </div>
            </div>
          </div>

          <div className='row'>
            <div className='col-lg-4'>
              <div className='fw-bold text-gray-600 mb-2 fs-7 text-uppercase'>Дата создания</div>
              <div className='fw-semibold fs-6 text-gray-800'>{formatDate(merchant.createdAt)}</div>
            </div>
            <div className='col-lg-4'>
              <div className='fw-bold text-gray-600 mb-2 fs-7 text-uppercase'>
                Дата обновления
              </div>
              <div className='fw-semibold fs-6 text-gray-800'>{formatDate(merchant.updatedAt)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Блок: Настройки мерчанта */}
      <MerchantSettings merchant={merchant} canEditMerchantSettings={canEditMerchantSettings} />

      {/* Блок: Белый список IP для API — только администратор и менеджер платёжки */}
      {canEditMerchantIpAllowlist && (
        <MerchantIpAllowlist
          merchantId={merchant.id}
          enabled={merchant.apiIpAllowlistEnabled ?? false}
          entries={merchant.apiIpAllowlist ?? []}
        />
      )}

      {/* Блок: Баланс кошелька Ledger */}
      <LedgerWalletBalanceCard ownerType='merchant' id={merchant.id} />

      {/* Блок 2: Платформа */}
      {merchant.platform && (
        <div className='card mb-7'>
          <div className='card-header border-0 pt-5'>
            <h3 className='card-title align-items-start flex-column'>
              <span className='card-label fw-bold fs-3 mb-1'>Платформа</span>
            </h3>
          </div>
          <div className='card-body py-4'>
            <div className='table-responsive'>
              <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                <thead>
                  <tr className='text-start text-muted fw-bold fs-7 text-uppercase gs-0'>
                    <th className='min-w-200px'>Логин</th>
                    <th className='min-w-200px'>Роль</th>
                    <th className='min-w-120px'>Статус</th>
                    <th className='min-w-150px'>Дата создания</th>
                    <th className='min-w-150px'>Дата обновления</th>
                    <th className='min-w-150px'>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  <EmployeeRow
                    employee={merchant.platform}
                    onResetPassword={canManageEmployees ? (employee) => handleResetPassword({ id: employee.id, uuid: employee.uuid }) : undefined}
                  />
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Блок 3: Привязанные операторы */}
      <div className='card'>
        <div className='card-header border-0 pt-5 d-flex justify-content-between align-items-center'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>Привязанные операторы</span>
            <span className='text-muted mt-1 fw-semibold fs-7'>
              Всего: {merchant.operators?.length ?? 0}
            </span>
          </h3>
          {canAddOperators && (
            <ModalAddOperator
              merchantId={merchant.id}
              merchantTitle={merchant.title}
              onAdded={() => fetchMerchant()}
            />
          )}
        </div>
        <div className='card-body py-4'>
          {merchant.operators && merchant.operators.length > 0 ? (
            <div className='table-responsive'>
              <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                <thead>
                  <tr className='text-start text-muted fw-bold fs-7 text-uppercase gs-0'>
                    <th className='min-w-200px'>Логин</th>
                    <th className='min-w-200px'>Роль</th>
                    <th className='min-w-120px'>Статус</th>
                    <th className='min-w-150px'>Дата создания</th>
                    <th className='min-w-150px'>Дата обновления</th>
                    <th className='min-w-150px'>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {merchant.operators.map((operator: IMerchantOperator) => (
                    <EmployeeRow
                      key={operator.uuid}
                      employee={operator}
                      onResetPassword={
                        canManageEmployees
                          ? (employee) => handleResetPassword({ id: employee.id, uuid: employee.uuid })
                          : undefined
                      }
                      onDetach={canDetachOperator(operator) ? handleDetachEmployee : undefined}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className='d-flex justify-content-center py-10'>
              <span className='text-muted fs-5'>Нет привязанных операторов</span>
            </div>
          )}
        </div>
      </div>

      {/* Блок 4: менеджеры мерчанта — вторая связь (merchants_managers), в список операторов
          она не попадает. Поле приходит только роли с ROLE_EMPLOYEE, поэтому блок виден
          admin/manager; менеджер мерчантов чужие и свои привязки этого типа не трогает. */}
      {canManageEmployees && (
        <div className='card mt-7'>
          <div className='card-header border-0 pt-5'>
            <h3 className='card-title align-items-start flex-column'>
              <span className='card-label fw-bold fs-3 mb-1'>Менеджеры мерчанта</span>
              <span className='text-muted mt-1 fw-semibold fs-7'>
                Всего: {merchant.merchantManagers?.length ?? 0}
              </span>
            </h3>
          </div>
          <div className='card-body py-4'>
            {merchant.merchantManagers && merchant.merchantManagers.length > 0 ? (
              <div className='table-responsive'>
                <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                  <thead>
                    <tr className='text-start text-muted fw-bold fs-7 text-uppercase gs-0'>
                      <th className='min-w-200px'>Логин</th>
                      <th className='min-w-200px'>Роль</th>
                      <th className='min-w-120px'>Статус</th>
                      <th className='min-w-150px'>Дата создания</th>
                      <th className='min-w-150px'>Дата обновления</th>
                      <th className='min-w-150px'>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {merchant.merchantManagers.map((manager: IMerchantOperator) => (
                      <EmployeeRow
                        key={manager.uuid}
                        employee={manager}
                        onResetPassword={(employee) =>
                          handleResetPassword({ id: employee.id, uuid: employee.uuid })
                        }
                        onDetach={handleDetachEmployee}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className='d-flex justify-content-center py-10'>
                <span className='text-muted fs-5'>Нет привязанных менеджеров</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default MerchantDetail
